import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format";

const SLOTS = [
  { key: "morning", label: "Morning", time: "10:00 AM – 12:00 PM" },
  { key: "afternoon", label: "Afternoon", time: "1:00 PM – 4:00 PM" },
  { key: "evening", label: "Evening", time: "5:00 PM – 7:00 PM" },
];

export const VisitScheduler = ({ property, onAuth }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("morning");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const isOwn = user?.id === property?.owner_id;

  const submit = async () => {
    if (!user) {
      onAuth?.("login");
      return;
    }
    if (!date) {
      toast.error("Pick a date");
      return;
    }
    setBusy(true);
    const iso = date.toISOString().slice(0, 10);
    try {
      await api.post("/visits", { property_id: property.id, date: iso, time_slot: slot, notes });
      toast.success("Visit requested — the owner will confirm soon.");
      setOpen(false);
      setDate(undefined);
      setNotes("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to book");
    } finally {
      setBusy(false);
    }
  };

  if (isOwn) return null; // owners don't book visits on their own listing

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost w-full" data-testid="book-visit-btn">
          <CalendarIcon className="w-4 h-4" /> Book a Visit
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="visit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Book a visit</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-muted)] mb-1">Pick a date</div>
            <Popover>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center justify-between btn-ghost" data-testid="visit-date-trigger">
                  <span>{date ? date.toLocaleDateString() : "Select date"}</span>
                  <CalendarIcon className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-muted)] mb-1">Pick a slot</div>
            <div className="grid grid-cols-3 gap-2" data-testid="visit-slots">
              {SLOTS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSlot(s.key)}
                  data-testid={`slot-${s.key}`}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    slot === s.key
                      ? "border-[var(--primary)] bg-[rgba(200,90,50,0.06)]"
                      : "border-[var(--line)] hover:border-[var(--line-strong)]"
                  }`}
                >
                  <div className="font-medium text-sm text-[var(--teak)]">{s.label}</div>
                  <div className="text-[10px] text-[var(--ink-muted)] mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {s.time}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-muted)] mb-1">Notes (optional)</div>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific questions or preferences?"
              data-testid="visit-notes"
            />
          </div>

          <button onClick={submit} disabled={busy || !date} className="btn-primary w-full" data-testid="visit-submit">
            {busy ? "Booking…" : "Request Visit"}
          </button>
          <p className="text-[10px] text-[var(--ink-muted)] text-center">
            The owner receives a notification and must confirm. You can track status under Dashboard → Visits.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
